import { useState, useEffect, useRef } from 'react';
import { notifications } from '@mantine/notifications';
import DashBoardApi from '../../api/DashBoardApi';
import type {
  DashboardProfile,
  BusinessDashboardData,
  MarketingDashboardOverviewData,
  MarketingDashboardDiagnosisData,
} from './dashboardTypes';

/**
 * Builds time parameters in the same format used across all Visiopt APIs.
 * SelectTimePicker outputs:
 *   - A numeric value (1=Today, 2=Yesterday, 5=Last7, 6=Last30, -1=AllTime, etc.)
 *   - When custom range (-2): from/to as MM-DD-YYYY strings
 *
 * The backend uses buildTimeFilter() to parse these, so we pass them through as-is.
 */
function buildTimeParams(timePickerValue: any, fromDate: string, toDate: string) {
  const time = timePickerValue ?? -1;

  // Custom range: SelectTimePicker sets time=-2 and provides from/to as MM-DD-YYYY
  if ((time === -2 || time === '-2' || String(time) === '-2') && fromDate && toDate) {
    return { time: -2, from: fromDate, to: toDate };
  }

  // Preset range: just pass the numeric time value, backend handles it
  return { time: Number(time) || -1 };
}

export function useDashboardData(
  profile: DashboardProfile | null,
  selectedWebsite: string,
  timePickerValue?: any,
  timePickerFromDate?: string,
  timePickerToDate?: string
) {
  const [businessData, setBusinessData] = useState<BusinessDashboardData | null>(null);
  const [marketingOverview, setMarketingOverview] = useState<MarketingDashboardOverviewData | null>(null);
  const [marketingDiagnosis, setMarketingDiagnosis] = useState<MarketingDashboardDiagnosisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [projectionPct, setProjectionPct] = useState(2.0);

  // Stable keys for detecting real changes
  const profileId = profile?.dashboard_profile_id || '';
  const dashboardType = profile?.dashboard_type || 'business';
  const campaignIdsKey = (profile?.settings?.selected_campaign_ids || []).join(',');
  const reportId = profile?.settings?.selected_report_id || '';
  // cost_threshold is a percentage (spec: default 20, range 5–50)
  const costThreshold = profile?.settings?.cost_threshold ?? 20;
  const goalId = (profile?.settings?.selected_goal_ids || [])[0] || '';
  const revenueGoalId = profile?.settings?.selected_revenue_goal_id || '';
  const conversionRateBenchmark = (profile?.settings as any)?.conversion_rate_benchmark ?? null;
  const costPerGoalBenchmark = (profile?.settings as any)?.cost_per_goal_benchmark ?? null;

  // Track previous website+profile to detect actual switches
  const prevKeyRef = useRef('');
  const currentKey = `${selectedWebsite}::${profileId}`;

  const prevTimePickerRef = useRef<any>(timePickerValue);

  // Use direct prop values as dependencies (not derived objects)
  const timeVal = String(timePickerValue ?? -1);
  const fromVal = String(timePickerFromDate ?? '');
  const toVal = String(timePickerToDate ?? '');

  // Debounce window (ms) applied to rapid consecutive changes — e.g. user
  // clicking multiple date range presets, or dragging the projection slider.
  // The loader only appears AFTER the user settles, and the API is only
  // called once per settled state.
  const DASHBOARD_FETCH_DEBOUNCE_MS = 400;

  // Single effect that handles everything: clear + debounced fetch
  useEffect(() => {
    if (prevKeyRef.current !== currentKey) {
      setBusinessData(null);
      setMarketingOverview(null);
      setMarketingDiagnosis(null);
      prevKeyRef.current = currentKey;
    }

    // No website at all: nothing to load, drop the loading state.
    if (!selectedWebsite) {
      setLoading(false);
      return;
    }

    if (!profileId) {
      return;
    }

    const prevTime = prevTimePickerRef.current;
    prevTimePickerRef.current = timePickerValue;
    const justOpenedRangeModal =
      (timePickerValue === -2 || String(timePickerValue) === '-2') &&
      prevTime !== -2 && String(prevTime) !== '-2' &&
      (!timePickerFromDate || !timePickerToDate);
    if (justOpenedRangeModal) {
      return;
    }

    const campaignIds = profile?.settings?.selected_campaign_ids || [];

    if ((timePickerValue === -2 || String(timePickerValue) === '-2') && (!timePickerFromDate || !timePickerToDate)) {
      return;
    }

    const timeParams = buildTimeParams(timePickerValue, timePickerFromDate ?? '', timePickerToDate ?? '');
    let cancelled = false;

    const willFetch =
      (dashboardType === 'business' || dashboardType === 'marketing') &&
      (campaignIds.length > 0 || !!reportId);
    setLoading(willFetch);

    // Debounce the whole fetch. If any dependency changes before the timer
    // fires, cleanup clears the timer so no stale request is started.
    const debounceId = setTimeout(() => {
      if (cancelled) return;

      if (dashboardType === 'business') {
        if (!campaignIds.length && !reportId) {
          setBusinessData(null);
          setLoading(false);
          return;
        }
        DashBoardApi.getBusinessDashboardPhase2Data({
          system_website_id: selectedWebsite,
          campaign_ids: campaignIds,
          cost_threshold: costThreshold,
          cost_per_goal_benchmark: costPerGoalBenchmark,
          report_id: reportId,
          goal_id: goalId || undefined,
          revenue_goal_id: revenueGoalId || undefined,
          ...timeParams,
        })
          .then((res: any) => res.json())
          .then((json: any) => {
            if (cancelled) return;
            setBusinessData(json?.status === 1 && json?.data ? json.data : null);
          })
          .catch(() => {
            if (cancelled) return;
            notifications.show({ color: 'red', message: 'Unable to load business metrics.' });
            setBusinessData(null);
          })
          .finally(() => {
            if (!cancelled) setLoading(false);
          });
        return;
      }

      if (dashboardType === 'marketing') {
        if (!campaignIds.length && !reportId) {
          setMarketingOverview(null);
          setMarketingDiagnosis(null);
          setLoading(false);
          return;
        }

        const fetchOverview = DashBoardApi.getMarketingDashboardPhase5Data({
          system_website_id: selectedWebsite,
          campaign_ids: campaignIds,
          cost_threshold: costThreshold,
          conversion_rate_benchmark: conversionRateBenchmark,
          report_id: reportId,
          goal_id: goalId || undefined,
          revenue_goal_id: revenueGoalId || undefined,
          ...timeParams,
        })
          .then((res: any) => res.json())
          .then((json: any) => {
            if (cancelled) return;
            setMarketingOverview(json?.status === 1 && json?.data ? json.data : null);
          })
          .catch(() => {
            if (cancelled) return;
            notifications.show({ color: 'red', message: 'Unable to load marketing metrics.' });
            setMarketingOverview(null);
          });

        const fetchDiagnosis = DashBoardApi.getMarketingDashboardPhase6Data({
          system_website_id: selectedWebsite,
          campaign_ids: campaignIds,
          cost_threshold: costThreshold,
          conversion_rate_benchmark: conversionRateBenchmark,
          improvement_pct: projectionPct,
          report_id: reportId,
          goal_id: goalId || undefined,
          revenue_goal_id: revenueGoalId || undefined,
          ...timeParams,
        })
          .then((res: any) => res.json())
          .then((json: any) => {
            if (cancelled) return;
            setMarketingDiagnosis(json?.status === 1 && json?.data ? json.data : null);
          })
          .catch(() => {
            if (cancelled) return;
            notifications.show({ color: 'red', message: 'Unable to load marketing diagnosis.' });
            setMarketingDiagnosis(null);
          });

        Promise.all([fetchOverview, fetchDiagnosis]).finally(() => {
          if (!cancelled) setLoading(false);
        });
      }
    }, DASHBOARD_FETCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(debounceId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWebsite, profileId, dashboardType, campaignIdsKey, reportId, costThreshold, conversionRateBenchmark, costPerGoalBenchmark, goalId, revenueGoalId, timeVal, fromVal, toVal, projectionPct]);

  return {
    businessData: businessData || ({} as BusinessDashboardData),
    marketingOverview: marketingOverview || ({} as MarketingDashboardOverviewData),
    marketingDiagnosis: marketingDiagnosis || ({} as MarketingDashboardDiagnosisData),
    loading,
    projectionPct,
    setProjectionPct,
  };
}
